import { ListSubheader, MenuItem, Select } from '@material-ui/core';
import { isNil } from 'lodash';
import React from 'react';
import { styled } from '~ui-kit/styled-components';
import { theme } from '~ui-kit/theme';
import { Box } from '../../box.component';
import { CircularProgressLoading } from '../../data-loading.component';
import { Subtitle4, Subtitle5, Subtitle6 } from '../../typography.component';
import { Category, Item } from './categorized-select-coverage.type';

interface Props {
  onChange: (category: string, value: number) => void;
  initialValue: number;
  categories: Category[];
  isLoading: boolean;
  hasTriggeredUpdate: boolean;
}

export const CategorizedSelectCoverage: React.FunctionComponent<Props> = ({ ...props }) => {
  if (props.isLoading && props.hasTriggeredUpdate) {
    return (
      <Box>
        <CircularProgressLoading />
      </Box>
    );
  }

  // Return category that contains the selected option
  const findCategory = (value: number): Category => {
    for (const category of props.categories) {
      const item: Item = findItem(value, category);
      if (!isNil(item)) return category;
    }
  };

  // Return option that is mapped to the value
  const findItem = (value: number, category: Category): Item => {
    for (const item of category.items) {
      if (item.value === value) {
        return item;
      }
    }
  };

  const hasSubCategories: boolean = props.categories.some((category: Category) => category.items.length > 1);
  return (
    <StyledSelect
      disabled={props.isLoading}
      disableUnderline={true}
      MenuProps={{
        MenuListProps: {
          style: {
            maxWidth: '310px',
            minWidth: '290px',
          },
        },
      }}
      value={props.initialValue}
      renderValue={(value: number) => {
        const selectedCategory: Category = findCategory(value);

        // If there are subcategories, display subcategory label. Otherwise display category label
        const text: string = hasSubCategories ? findItem(value, selectedCategory).label : selectedCategory.label;
        return <Subtitle4 fontWeight="bold">{text}</Subtitle4>;
      }}
    >
      {/* If a category contains mumtiple options, add category labels */}
      {hasSubCategories &&
        props.categories.map((category: Category) => {
          const items = category.items.map((item: Item) => {
            return (
              <StyledMenuItem
                disabled={item.value === props.initialValue}
                onClick={() => props.onChange(category.value, item.value)}
                value={item.value}
              >
                <Subtitle5 color={theme.colors.black}>{item.label}</Subtitle5>
              </StyledMenuItem>
            );
          });

          return [
            <ListSubheader>
              <Subtitle4 color={theme.colors.black} fontWeight={'bold'}>
                {category.label}
              </Subtitle4>
            </ListSubheader>,
            items,
          ];
        })}

      {/* If all category contain only one option, display the category label with the option */}
      {!props.categories.some((category) => category.items.length > 1) &&
        props.categories.map((category: Category) =>
          category.items.map((item: Item) => {
            return (
              <StyledMenuItem
                disabled={item.value === props.initialValue}
                onClick={() => props.onChange(category.value, item.value)}
                value={item.value}
              >
                <Box flexDirection={'column'}>
                  <Subtitle5 color={theme.colors.black}>{category.label}</Subtitle5>
                  <Subtitle6 fontSize={'12px'} color={theme.colors.black}>
                    {item.label}
                  </Subtitle6>
                </Box>
              </StyledMenuItem>
            );
          }),
        )}
    </StyledSelect>
  );
};

const StyledMenuItem = styled(MenuItem)`
  && {
    white-space: normal;
  }
`;

const StyledSelect = styled(Select)`
  && {
    .MuiSelect-root {
      padding-right: 32px;
    }

    .MuiSelect-select:focus {
      background: none;
    }

    .MuiSelect-icon {
      color: ${theme.colors.black};
    }

    .MuiSelect-icon.Mui-disabled {
      color: inherit;
    }
  }
`;
